import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { FaChevronDown, FaChevronRight, FaTrashAlt } from 'react-icons/fa'
import {
  useGetClientSavedOrdersQuery,
  useMarkClientSavedOrderMutation,
} from '../../../redux/clientData/apiClientData'
import { setPendingNoteSearch } from '../../../redux/searchSpares/sparesSlice'
import Loader from '../../loader/Loader'
import styles from './Notes.module.css'

const Notes = ({ rerender, onNavigateToSearch }) => {
  const dispatch = useDispatch()

  const {
    data: savedOrdersData,
    isLoading: isLoadingSavedOrders,
    refetch,
  } = useGetClientSavedOrdersQuery()

  const [markClientSavedOrder, { isLoading: isLoadingMark }] =
    useMarkClientSavedOrderMutation()

  const [collapsedGroups, setCollapsedGroups] = useState({})

  useEffect(() => {
    refetch()
  }, [refetch, rerender])

  const savedOrders = useMemo(
    () => savedOrdersData?.savedOrders || [],
    [savedOrdersData]
  )

  const hasPrice = useMemo(
    () => savedOrders.some((order) => order.price != null),
    [savedOrders]
  )
  const hasRetailPrice = useMemo(
    () => savedOrders.some((order) => order.retailPrice != null),
    [savedOrders]
  )

  const hasAnyPrice = hasPrice || hasRetailPrice

  const unitPrice = (order) => {
    if (order.price != null) return Number(order.price)
    if (order.retailPrice != null) return Number(order.retailPrice)
    return null
  }

  const lineTotal = (order) => {
    const unit = unitPrice(order)
    if (unit == null || Number.isNaN(unit)) return null
    return unit * Number(order.requiredQty || 0)
  }

  const groupTotal = (orders) =>
    orders.reduce((acc, order) => {
      const total = lineTotal(order)
      return total == null ? acc : acc + total
    }, 0)

  const groups = useMemo(() => {
    const grouped = savedOrders.reduce((acc, order) => {
      const key = order.groupName || 'Без групи'
      if (!acc[key]) acc[key] = []
      acc[key].push(order)
      return acc
    }, {})
    return Object.entries(grouped)
  }, [savedOrders])

  const toggleGroup = (groupName) => {
    setCollapsedGroups((prev) => ({ ...prev, [groupName]: !prev[groupName] }))
  }

  const handleNoteClick = (order) => {
    if (!order.brand || !order.code) {
      toast.error('Недостатньо даних для пошуку...')
      return
    }
    dispatch(setPendingNoteSearch({ brand: order.brand, code: order.code }))
    onNavigateToSearch?.()
  }

  const handleDeleteNote = async (savedOrderID) => {
    try {
      await markClientSavedOrder({ savedOrderID }).unwrap()
      toast.success('Видалено з нотаток')
      refetch()
    } catch (error) {
      toast.error('Сталась помилка...')
    }
  }

  if (isLoadingSavedOrders) return <Loader />

  return (
    <div className={styles.notesPageContainer}>
      {isLoadingMark && <Loader />}
      {groups.length > 0 ? (
        groups.map(([groupName, orders]) => {
          const isCollapsed = collapsedGroups[groupName]
          return (
            <div key={groupName} className={styles.groupContainer}>
              <div
                className={styles.groupHeader}
                onClick={() => toggleGroup(groupName)}
              >
                <span className={styles.groupChevron}>
                  {isCollapsed ? <FaChevronRight /> : <FaChevronDown />}
                </span>
                <span className={styles.groupTitle}>{groupName}</span>
                {hasAnyPrice && (
                  <span className={styles.groupTotal}>
                    Сума: {groupTotal(orders).toFixed(2)}
                  </span>
                )}
                <span className={styles.groupCount}>{orders.length}</span>
              </div>
              {!isCollapsed && (
                <table className={styles.notesTable}>
                  <thead>
                    <tr>
                      <th>Бренд</th>
                      <th>Номер</th>
                      <th>Опис</th>
                      <th>Примітка</th>
                      <th>К-сть</th>
                      {hasRetailPrice && <th>Роздріб</th>}
                      {hasPrice && <th>Ціна</th>}
                      {hasAnyPrice && <th>Сума</th>}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr
                        key={order.savedOrderID}
                        className={styles.notesRow}
                        onClick={() => handleNoteClick(order)}
                      >
                        <td>{order.brand}</td>
                        <td>{order.code}</td>
                        <td>{order.description}</td>
                        <td>{order.note}</td>
                        <td>{order.requiredQty}</td>
                        {hasRetailPrice && <td>{order.retailPrice}</td>}
                        {hasPrice && <td>{order.price}</td>}
                        {hasAnyPrice && (
                          <td>
                            {lineTotal(order) == null
                              ? ''
                              : lineTotal(order).toFixed(2)}
                          </td>
                        )}
                        <td>
                          <button
                            className={styles.deleteBtn}
                            title="Видалити з нотаток"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteNote(order.savedOrderID)
                            }}
                          >
                            <FaTrashAlt />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {hasAnyPrice && (
                    <tfoot>
                      <tr className={styles.totalRow}>
                        <td colSpan={5}>Разом</td>
                        {hasRetailPrice && <td></td>}
                        {hasPrice && <td></td>}
                        <td>{groupTotal(orders).toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}
            </div>
          )
        })
      ) : (
        <h3 className={styles.noNotesText}>Нотаток поки немає</h3>
      )}
    </div>
  )
}

export default Notes
